/* ============================================================================
   Pure Shenandoah -- mobile nav accordion controller.

   Owns only the in-panel disclosures (Shop). The panel itself -- open/close,
   Escape, focus trap, scroll lock -- stays with Dawn's <header-drawer>.

   Two reasons this exists rather than leaning on <details> alone:

   1. A <details> cannot animate closed. The browser hides the panel the instant
      `open` is removed, so the 500ms collapse the source animates has to be
      driven here, with `open` held until the animation finishes.

   2. Dawn's MenuDrawer binds a click handler to *every* <summary> inside
      <header-drawer> and treats it as a sliding submenu: it traps focus inside
      the panel. That is right for a drawer submenu and wrong for an inline
      accordion, which you must be able to tab straight past.

   Measured off live pureshenandoah.com with tools/migration/nav-anim.mjs:
   height 0 -> content over 500ms, `ease`, and the same in reverse on close.
   ============================================================================ */

const PURE_MNAV_DURATION = 500;

class PureMobileNav extends HTMLElement {
  connectedCallback() {
    // Section re-render in the Theme Editor replaces the element, but a move
    // within the DOM would re-run this on the same instance.
    if (this.bound) return;
    this.bound = true;

    this.disclosures = Array.from(this.querySelectorAll('details.pure-mnav__disclosure'));
    if (!this.disclosures.length) return;

    /* Capture phase, on this element rather than on the summary: Dawn's listener
       is bound directly to the summary, so an at-target listener of ours would
       fire in registration order and lose the race whenever global.js upgraded
       <header-drawer> first. An ancestor capture listener always runs before
       both, whichever script loaded first. */
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick, true);

    /* Dawn closes these from the outside too -- closeMenuDrawer() strips `open`
       from every nested <details>, and closeSubmenu() runs on Escape. Syncing
       from the element's own toggle event covers those paths as well as ours. */
    this.onToggle = this.onToggle.bind(this);
    this.disclosures.forEach((details) => {
      details.addEventListener('toggle', this.onToggle);
      this.onToggle({ target: details });
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick, true);
    this.disclosures?.forEach((details) => details.removeEventListener('toggle', this.onToggle));
    this.animations?.forEach((animation) => animation.cancel());
  }

  onToggle(event) {
    const details = event.target;
    const summary = details.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', String(details.open));
    // Dawn strips `open` directly on Escape and on drawer close, without going
    // through toggle(), so the closing flag has to be cleared from the state.
    if (!details.open) details.classList.remove('is-closing');
  }

  onClick(event) {
    const summary = event.target.closest('summary');
    if (!summary) return;

    const details = summary.parentElement;
    if (!this.disclosures.includes(details)) return;

    // Dawn's summary handler never runs, and we own the native toggle so the
    // close animation has something to play before the panel is hidden.
    event.stopPropagation();
    event.preventDefault();
    this.toggle(details);
  }

  toggle(details) {
    const panel = details.querySelector('.pure-mnav__panel');
    if (!panel) {
      details.open = !details.open;
      return;
    }

    this.animations = this.animations || new Map();
    this.animations.get(panel)?.cancel();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.classList.remove('is-closing');
      details.open = !details.open;
      return;
    }

    const opening = !details.open;
    // Height has to be read while the panel is rendered, so open first and let
    // the animation start from zero.
    if (opening) details.open = true;

    /* `open` stays true for the length of the collapse, so anything keyed off it
       -- the chevron -- would sit frozen until the animation ended. This marks
       the intent rather than the state, and flips on the first frame. */
    details.classList.toggle('is-closing', !opening);

    const height = panel.scrollHeight;
    const frames = opening ? [{ height: '0px' }, { height: `${height}px` }] : [{ height: `${height}px` }, { height: '0px' }];

    const animation = panel.animate(frames, { duration: PURE_MNAV_DURATION, easing: 'ease' });
    this.animations.set(panel, animation);

    animation.finished
      .then(() => {
        if (!opening) {
          details.open = false;
          details.classList.remove('is-closing');
        }
        this.animations.delete(panel);
      })
      // A cancel (rapid re-toggle) rejects the promise; the replacement
      // animation already owns the element, so there is nothing to clean up.
      .catch(() => {});
  }
}

if (!customElements.get('pure-mobile-nav')) {
  customElements.define('pure-mobile-nav', PureMobileNav);
}
